import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useAuthStore } from '../store/auth.store';
import { usePermissions } from '../hooks/usePermissions';
import { OrdersService } from '../services/orders/orders.service';
import { UsersService } from '../services/users/users.service';
import { CashRegisterService, type CierreResponse } from '../services/cash-register/cash-register.service';
import { DatePickerInput } from '../components/ui/DatePickerInput';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import {
  DollarSign,
  CreditCard,
  Banknote,
  Smartphone,
  Tag,
  Loader2,
  RefreshCcw,
  Printer,
  User,
  Package,
  X,
  CheckCircle2,
  Lock,
  ChevronDown,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TicketCierreCaja } from '../components/pos/TicketCierreCaja';

interface Venta {
  id: string;
  numeroPedido: string;
  cliente: { nombre: string; email: string; telefono: string };
  totales: { subtotal: number; envio: number; descuento: number; total: number };
  estado: string;
  metodoPago?: string;
  pagos?: Array<{ metodo: string; monto: number }>;
  items: Array<{ nombreProducto: string; nombrePresentacion?: string; cantidad: number; precioUnitario: number; subtotal: number }>;
  fechas: { creadoEn: string };
  motivoCancelacion?: string;
}

const metodoLabels: Record<string, { label: string; icon: React.ComponentType<any> }> = {
  EFECTIVO: { label: 'Efectivo', icon: Banknote },
  TARJETA: { label: 'Tarjeta', icon: CreditCard },
  NEQUI: { label: 'Nequi', icon: Smartphone },
  DAVIPLATA: { label: 'Daviplata', icon: Smartphone },
  TRANSFERENCIA: { label: 'Transferencia', icon: Tag },
  CARD: { label: 'Tarjeta', icon: CreditCard },
  PSE: { label: 'PSE', icon: Tag },
  BANCOLOMBIA_TRANSFER: { label: 'Bancolombia', icon: Tag },
  EFECTY: { label: 'Efecty', icon: Tag },
};

export const CierreCajaPage: React.FC = () => {
  const { user } = useAuthStore();
  const { isAdmin } = usePermissions();
  const admin = isAdmin();
  const [ventas, setVentas] = useState<Venta[]>([]);
  const [loading, setLoading] = useState(false);
  const [fecha, setFecha] = useState(() => {
    const hoy = new Date();
    const yyyy = hoy.getFullYear();
    const mm = String(hoy.getMonth() + 1).padStart(2, '0');
    const dd = String(hoy.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [activeTab, setActiveTab] = useState<'ventas' | 'cancelaciones'>('ventas');
  const [vendedores, setVendedores] = useState<Array<{ id: string; nombreCompleto: string }>>([]);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState<string>('');
  const [metodoPagoFiltro, setMetodoPagoFiltro] = useState<string>('');

  const metodosFiltro = [
    { id: '', label: 'Todos los métodos' },
    { id: 'EFECTIVO', label: 'Efectivo', icon: Banknote },
    { id: 'TARJETA', label: 'Tarjeta', icon: CreditCard },
    { id: 'NEQUI', label: 'Nequi', icon: Smartphone },
    { id: 'DAVIPLATA', label: 'Daviplata', icon: Smartphone },
    { id: 'TRANSFERENCIA', label: 'Transferencia', icon: Tag },
  ];

  // Modal de cierre de caja
  const [mostrarModalCierre, setMostrarModalCierre] = useState(false);
  const [dineroContado, setDineroContado] = useState({
    efectivo: '',
    tarjeta: '',
    transferencias: '',
    nequi: '',
  });
  const [cierreGuardado, setCierreGuardado] = useState(false);
  const [cierreExistente, setCierreExistente] = useState<CierreResponse['data']['cierre']>(null);
  const [verificandoCierre, setVerificandoCierre] = useState(false);
  const [mostrarConfirmacionImprimir, setMostrarConfirmacionImprimir] = useState(false);
  const [opcionImprimirIndex, setOpcionImprimirIndex] = useState(0);

  const cargarVendedores = async () => {
    try {
      const result = await UsersService.getUsers(1, 500);
      if (result.data) {
        setVendedores(result.data.map((u: any) => ({ id: u.id, nombreCompleto: u.nombreCompleto })));
      }
    } catch {
      // Silencioso
    }
  };

  const verificarCierre = async () => {
    setVerificandoCierre(true);
    try {
      const vendedorId = admin ? (vendedorSeleccionado || user?.id) : user?.id;
      const result = await CashRegisterService.checkStatus(fecha, vendedorId || undefined);
      if (result.success) {
        setCierreExistente(result.data.cierre);
      }
    } catch {
      // Silencioso: si falla, asumimos que no hay cierre
      setCierreExistente(null);
    } finally {
      setVerificandoCierre(false);
    }
  };

  const cargarVentas = async () => {
    setLoading(true);
    try {
      const desde = `${fecha}T05:00:00.000Z`;
      const hasta = `${fecha}T04:59:59.999Z`;
      const hastaDate = new Date(hasta);
      hastaDate.setDate(hastaDate.getDate() + 1);
      const hastaStr = hastaDate.toISOString();
      const params: any = { desde, hasta: hastaStr, limit: 500 };
      let vendedorId: string | undefined;
      if (admin) {
        vendedorId = vendedorSeleccionado || undefined;
      } else {
        vendedorId = user?.id;
      }
      if (vendedorId) {
        params.vendedorId = vendedorId;
      }
      const response = await OrdersService.getMySales(params);
      if (response.success) {
        setVentas(response.data || []);
      } else {
        toast.error(response.message || 'Error al cargar ventas');
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al cargar ventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admin) {
      cargarVendedores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => {
    if (!user?.id) return;
    cargarVentas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, vendedorSeleccionado, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    verificarCierre();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, vendedorSeleccionado, user?.id]);

  useEffect(() => {
    setMetodoPagoFiltro('');
  }, [fecha, vendedorSeleccionado]);

  const ventasEfectivas = useMemo(() => {
    let filtradas = ventas.filter(v => v.estado !== 'CANCELLED');
    if (metodoPagoFiltro) {
      filtradas = filtradas.filter(v =>
        v.metodoPago === metodoPagoFiltro ||
        (v.pagos && v.pagos.some(p => p.metodo === metodoPagoFiltro))
      );
    }
    return filtradas;
  }, [ventas, metodoPagoFiltro]);
  const ventasCanceladas = useMemo(() => {
    let filtradas = ventas.filter(v => v.estado === 'CANCELLED');
    if (metodoPagoFiltro) {
      filtradas = filtradas.filter(v =>
        v.metodoPago === metodoPagoFiltro ||
        (v.pagos && v.pagos.some(p => p.metodo === metodoPagoFiltro))
      );
    }
    return filtradas;
  }, [ventas, metodoPagoFiltro]);

  // Extrae los pagos válidos de una venta (fallback a metodoPago si los pagos están corruptos)
  const getPagosValidos = (v: Venta) => {
    const ventaTotal = v.totales?.total || 0;
    const pagosValidos = v.pagos?.filter(p => p.metodo && p.monto > 0) || [];
    const sumaPagos = pagosValidos.reduce((s, p) => s + p.monto, 0);
    const usarPagos = pagosValidos.length > 0 && Math.abs(sumaPagos - ventaTotal) <= 1;
    return usarPagos ? pagosValidos : [{ metodo: v.metodoPago || 'OTRO', monto: ventaTotal }];
  };

  const resumen = useMemo(() => {
    // Helpers para clasificar métodos
    const esEfectivo = (m: string) => m === 'EFECTIVO';
    const esTarjeta = (m: string) => m === 'TARJETA' || m === 'CARD';
    const esTransferencia = (m: string) => m === 'TRANSFERENCIA' || m === 'PSE' || m === 'BANCOLOMBIA_TRANSFER';
    const esNequi = (m: string) => m === 'NEQUI' || m === 'DAVIPLATA';

    let total = 0;
    let totalEfectivo = 0;
    let totalTarjeta = 0;
    let totalTransferencias = 0;
    let totalNequi = 0;
    const porMetodo: Record<string, { count: number; total: number }> = {};

    ventasEfectivas.forEach(v => {
      const ventaTotal = v.totales?.total || 0;
      total += ventaTotal;

      const pagos = getPagosValidos(v);
      pagos.forEach(p => {
        const metodo = p.metodo || 'OTRO';
        const monto = p.monto || 0;

        if (esEfectivo(metodo)) totalEfectivo += monto;
        else if (esTarjeta(metodo)) totalTarjeta += monto;
        else if (esTransferencia(metodo)) totalTransferencias += monto;
        else if (esNequi(metodo)) totalNequi += monto;

        if (!porMetodo[metodo]) porMetodo[metodo] = { count: 0, total: 0 };
        porMetodo[metodo].count += 1;
        porMetodo[metodo].total += monto;
      });
    });

    return { total, totalEfectivo, totalTarjeta, totalTransferencias, totalNequi, porMetodo, cantidad: ventasEfectivas.length };
  }, [ventasEfectivas]);

  // Total mostrado en la tabla: respeta el filtro de método (solo suma el monto del método filtrado en pagos mixtos)
  const totalTabla = useMemo(() => {
    if (!metodoPagoFiltro) return resumen.total;
    return ventasEfectivas.reduce((sum, v) => {
      const pagos = getPagosValidos(v);
      const pago = pagos.find(p => p.metodo === metodoPagoFiltro);
      return sum + (pago?.monto || 0);
    }, 0);
  }, [ventasEfectivas, metodoPagoFiltro, resumen.total]);

  // Comparación del cierre
  const comparacion = useMemo(() => {
    const contadoEfectivo = Number(dineroContado.efectivo) || 0;
    const contadoTarjeta = Number(dineroContado.tarjeta) || 0;
    const contadoTransferencias = Number(dineroContado.transferencias) || 0;
    const contadoNequi = Number(dineroContado.nequi) || 0;
    const totalContado = contadoEfectivo + contadoTarjeta + contadoTransferencias + contadoNequi;

    const difEfectivo = contadoEfectivo - resumen.totalEfectivo;
    const difTarjeta = contadoTarjeta - resumen.totalTarjeta;
    const difTransferencias = contadoTransferencias - resumen.totalTransferencias;
    const difNequi = contadoNequi - resumen.totalNequi;
    const diferenciaTotal = totalContado - resumen.total;

    return {
      contadoEfectivo,
      contadoTarjeta,
      contadoTransferencias,
      contadoNequi,
      totalContado,
      difEfectivo,
      difTarjeta,
      difTransferencias,
      difNequi,
      diferenciaTotal,
    };
  }, [dineroContado, resumen]);

  const abrirModalCierre = () => {
    setDineroContado({ efectivo: '', tarjeta: '', transferencias: '', nequi: '' });
    setCierreGuardado(false);
    setMostrarModalCierre(true);
    setMostrarConfirmacionImprimir(false);
  };

  const imprimirTicket = () => {
    const ticket = document.getElementById('ticket-cierre');
    if (!ticket) return;
    const html = ticket.innerHTML;
    const w = window.open('', '_blank', 'width=320,height=600');
    if (!w) { toast.error('Permita las ventanas emergentes para imprimir'); return; }
    w.document.write(`
<!DOCTYPE html>
<html>
<head>
<title>Cierre ${fecha}</title>
<style>
@page { size: 80mm auto; margin: 0; }
body { margin: 0; padding: 3mm; font-family: 'Courier New', Courier, monospace; font-size: 10pt; line-height: 1.3; color: #000; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.thermal-ticket { width: 72mm; max-width: 72mm; }
.ticket-header { text-align: center; margin-bottom: 2mm; }
.ticket-title { font-size: 14pt; font-weight: bold; margin: 0 0 1mm 0; letter-spacing: 0.5px; }
.ticket-subtitle { font-size: 9pt; margin: 0 0 1mm 0; }
.ticket-info { font-size: 8pt; margin: 0; line-height: 1.4; }
.ticket-divider { text-align: center; font-size: 8pt; margin: 2mm 0; letter-spacing: -1px; }
.ticket-section { margin-bottom: 1mm; }
.ticket-row { display: flex; justify-content: space-between; margin: 0.5mm 0; }
.ticket-label { font-weight: bold; }
.ticket-value { text-align: right; }
.ticket-items { margin: 2mm 0; }
.ticket-item-header { display: flex; justify-content: space-between; font-size: 8pt; font-weight: bold; border-bottom: 1px dashed #000; padding-bottom: 1mm; margin-bottom: 1mm; }
.ticket-item { margin-bottom: 1.5mm; }
.ticket-item-name { font-size: 9pt; word-wrap: break-word; }
.ticket-item-row { display: flex; justify-content: space-between; font-size: 9pt; }
.ticket-col-desc { flex: 1; }
.ticket-col-qty { width: 35mm; text-align: right; }
.ticket-col-total { width: 20mm; text-align: right; font-weight: bold; }
.ticket-totals { margin: 2mm 0; }
.ticket-total-row { font-size: 11pt; font-weight: bold; border-top: 1px solid #000; padding-top: 1mm; margin-top: 1mm; }
.ticket-footer { text-align: center; margin-top: 4mm; padding-top: 2mm; border-top: 1px dashed #000; }
.ticket-footer p { margin: 0.5mm 0; font-size: 9pt; }
.ticket-small { font-size: 7pt; color: #444; }
</style>
</head>
<body>
<div class="thermal-ticket">${html}</div>
</body>
</html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  const confirmarCierre = async () => {
    try {
      const vendedorId = admin ? (vendedorSeleccionado || user?.id) : user?.id;
      await CashRegisterService.close({
        fecha,
        vendedorId: vendedorId || undefined,
        totalVentas: resumen.total,
        totalContado: comparacion.totalContado,
        diferenciaTotal: comparacion.diferenciaTotal,
        efectivoVentas: resumen.totalEfectivo,
        efectivoContado: comparacion.contadoEfectivo,
        tarjetaVentas: resumen.totalTarjeta,
        tarjetaContado: comparacion.contadoTarjeta,
        transferenciasVentas: resumen.totalTransferencias,
        transferenciasContado: comparacion.contadoTransferencias,
        nequiVentas: resumen.totalNequi,
        nequiContado: comparacion.contadoNequi,
        cantidadVentas: resumen.cantidad,
      });
      setCierreGuardado(true);
      setCierreExistente({} as any); // Marcamos como cerrado
      if (comparacion.diferenciaTotal === 0) {
        toast.success('✅ Caja cuadrada perfectamente');
      } else if (comparacion.diferenciaTotal > 0) {
        toast.success(`📥 Cierre registrado. Hay un sobrante de $${comparacion.diferenciaTotal.toLocaleString()}`);
      } else {
        toast.error(`⚠️ Cierre registrado. Hay un faltante de $${Math.abs(comparacion.diferenciaTotal).toLocaleString()}`);
      }
      setMostrarModalCierre(false);
      setMostrarConfirmacionImprimir(true);
      setOpcionImprimirIndex(0);
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('⚠️ Esta fecha ya tiene un cierre de caja registrado');
        setCierreExistente({} as any);
      } else {
        toast.error(error.response?.data?.message || 'Error al registrar el cierre de caja');
      }
    }
  };

  const getDiferenciaColor = (valor: number) => {
    if (valor === 0) return 'text-emerald-600';
    if (valor > 0) return 'text-blue-600';
    return 'text-red-600';
  };

  const getDiferenciaLabel = (valor: number) => {
    if (valor === 0) return 'Cuadre';
    if (valor > 0) return 'Sobrante';
    return 'Faltante';
  };

  // Teclado para modal de confirmación de impresión
  useEffect(() => {
    if (!mostrarConfirmacionImprimir) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpcionImprimirIndex((prev) => (prev === 0 ? 1 : 0));
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpcionImprimirIndex((prev) => (prev === 0 ? 1 : 0));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (opcionImprimirIndex === 0) {
          imprimirTicket();
        }
        setMostrarConfirmacionImprimir(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mostrarConfirmacionImprimir, opcionImprimirIndex]);

  return (
    <div className="min-h-screen p-6 bg-gray-50 text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Cierre de Caja
            {cierreExistente && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="w-3 h-3" />
                Cerrado
              </span>
            )}
            {verificandoCierre && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                <Loader2 className="w-3 h-3 animate-spin" />
                Verificando...
              </span>
            )}
          </h1>
          {user && (
            <p className="text-sm mt-1 text-gray-500">
              <User className="w-4 h-4 inline mr-1" />
              Vendedor: <span className="font-medium">{user.nombreCompleto}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {admin && (
            <Listbox value={vendedorSeleccionado} onChange={setVendedorSeleccionado}>
              <div className="relative">
                <ListboxButton className="relative w-[200px] cursor-pointer rounded-xl border border-gray-200 bg-white py-2.5 pl-3.5 pr-10 text-left text-sm text-gray-900 shadow-sm hover:border-emerald-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
                  <span className="block truncate">
                    {vendedorSeleccionado ? vendedores.find((v) => v.id === vendedorSeleccionado)?.nombreCompleto : 'Todos los vendedores'}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions
                  transition
                  className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <ListboxOption
                    value=""
                    className="group relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                  >
                    <span className="block truncate">Todos los vendedores</span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                      <Check className="h-4 w-4" />
                    </span>
                  </ListboxOption>
                  {vendedores.map((v) => (
                    <ListboxOption
                      key={v.id}
                      value={v.id}
                      className="group relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                    >
                      <span className="block truncate">{v.nombreCompleto}</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                        <Check className="h-4 w-4" />
                      </span>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          )}
          <Listbox value={metodoPagoFiltro} onChange={setMetodoPagoFiltro}>
            <div className="relative">
              <ListboxButton className="relative w-[200px] cursor-pointer rounded-xl border border-gray-200 bg-white py-2.5 pl-3.5 pr-10 text-left text-sm text-gray-900 shadow-sm hover:border-emerald-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
                <span className="block truncate">
                  {metodosFiltro.find((m) => m.id === metodoPagoFiltro)?.label || 'Todos los métodos'}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>
              <ListboxOptions
                transition
                className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {metodosFiltro.map((m) => (
                  <ListboxOption
                    key={m.id}
                    value={m.id}
                    className="group relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                  >
                    <span className="block truncate">{m.label}</span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                      <Check className="h-4 w-4" />
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>
          <DatePickerInput
            value={fecha}
            onChange={setFecha}
            placeholder="Seleccionar fecha"
          />
          <button
            onClick={cargarVentas}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Actualizar
          </button>
          {!cierreExistente && (
            <button
              onClick={abrirModalCierre}
              disabled={ventasEfectivas.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-colors shadow-md"
            >
              <Lock className="w-4 h-4" />
              Cerrar Caja
            </button>
          )}
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="p-4 rounded-xl border bg-white border-gray-200">
          <p className="text-xs uppercase tracking-wide text-gray-500">Total Ventas Efectivas</p>
          <p className="text-2xl font-bold text-emerald-600">${totalTabla.toLocaleString()}</p>
          <p className="text-xs mt-1 text-gray-500">{resumen.cantidad} ventas</p>
        </div>
        <div className="p-4 rounded-xl border bg-white border-gray-200">
          <p className="text-xs uppercase tracking-wide text-gray-500">Efectivo</p>
          <p className="text-2xl font-bold text-blue-600">${resumen.totalEfectivo.toLocaleString()}</p>
          <p className="text-xs mt-1 text-gray-500">
            {ventasEfectivas.filter(v => v.metodoPago === 'EFECTIVO' || (v.pagos && v.pagos.some(p => p.metodo === 'EFECTIVO'))).length} ventas
          </p>
        </div>
        <div className="p-4 rounded-xl border bg-white border-gray-200">
          <p className="text-xs uppercase tracking-wide text-gray-500">Tarjetas</p>
          <p className="text-2xl font-bold text-purple-600">${resumen.totalTarjeta.toLocaleString()}</p>
          <p className="text-xs mt-1 text-gray-500">
            {ventasEfectivas.filter(v => v.metodoPago === 'TARJETA' || v.metodoPago === 'CARD' || (v.pagos && v.pagos.some(p => p.metodo === 'TARJETA' || p.metodo === 'CARD'))).length} ventas
          </p>
        </div>
        <div className="p-4 rounded-xl border bg-white border-gray-200">
          <p className="text-xs uppercase tracking-wide text-gray-500">Transferencias / Otros</p>
          <p className="text-2xl font-bold text-amber-600">${(resumen.totalTransferencias + resumen.totalNequi).toLocaleString()}</p>
          <p className="text-xs mt-1 text-gray-500">
            {ventasEfectivas.filter(v =>
              (v.metodoPago && v.metodoPago !== 'EFECTIVO' && v.metodoPago !== 'TARJETA' && v.metodoPago !== 'CARD') ||
              (v.pagos && v.pagos.some(p => p.metodo !== 'EFECTIVO' && p.metodo !== 'TARJETA' && p.metodo !== 'CARD'))
            ).length} ventas
          </p>
        </div>
        <div className="p-4 rounded-xl border bg-white border-gray-200">
          <p className="text-xs uppercase tracking-wide text-gray-500">Ventas Anuladas</p>
          <p className="text-2xl font-bold text-red-500">${ventasCanceladas.reduce((sum, v) => sum + (v.totales?.total || 0), 0).toLocaleString()}</p>
          <p className="text-xs mt-1 text-gray-500">{ventasCanceladas.length} anulaciones</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-xl border overflow-hidden bg-white border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('ventas')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'ventas'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            Ventas ({ventasEfectivas.length})
          </button>
          <button
            onClick={() => setActiveTab('cancelaciones')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'cancelaciones'
                ? 'border-red-500 text-red-500'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="w-4 h-4" />
            Cancelaciones ({ventasCanceladas.length})
          </button>
        </div>

        <div className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : activeTab === 'ventas' ? (
            ventasEfectivas.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">No hay ventas registradas para esta fecha.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Pedido</th>
                      <th className="px-4 py-3 text-left font-medium">Cliente</th>
                      <th className="px-4 py-3 text-left font-medium">Productos</th>
                      <th className="px-4 py-3 text-left font-medium">Método</th>
                      <th className="px-4 py-3 text-right font-medium">Total</th>
                      <th className="px-4 py-3 text-left font-medium">Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasEfectivas.map((venta) => {
                      const mp = metodoLabels[venta.metodoPago || 'OTRO'] || { label: venta.metodoPago || 'N/A', icon: Tag };
                      const Icon = mp.icon;
                      const pagos = getPagosValidos(venta);
                      const esMixta = pagos.length > 1;
                      const montoFila = metodoPagoFiltro && esMixta
                        ? (pagos.find(p => p.metodo === metodoPagoFiltro)?.monto || 0)
                        : (venta.totales?.total || 0);
                      return (
                        <tr key={venta.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-mono font-medium text-emerald-600">{venta.numeroPedido}</span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium">{venta.cliente?.nombre}</p>
                            <p className="text-xs text-gray-500">{venta.cliente?.telefono}</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-0.5">
                              {venta.items?.slice(0, 2).map((item, idx) => (
                                <p key={idx} className="text-xs">{item.cantidad}x {item.nombreProducto} <span className="text-emerald-600 font-medium">({item.nombrePresentacion || 'unidad'})</span></p>
                              ))}
                              {(venta.items?.length || 0) > 2 && (
                                <p className="text-xs text-gray-500">+{(venta.items!.length - 2)} más</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {esMixta ? (
                              <div className="space-y-0.5">
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">
                                  <DollarSign className="w-3 h-3" /> Pago mixto
                                </span>
                                <div className="text-[10px] text-gray-500 leading-tight">
                                  {pagos.map((p, i) => (
                                    <span key={i}>{p.metodo}: ${p.monto.toLocaleString()}{i < pagos.length - 1 ? ' · ' : ''}</span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <Icon className="w-3 h-3" /> {mp.label}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-bold">${montoFila.toLocaleString()}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {new Date(venta.fechas?.creadoEn).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-right">TOTAL</td>
                      <td className="px-4 py-3 text-right text-emerald-600">${totalTabla.toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          ) : (
            ventasCanceladas.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">No hay cancelaciones registradas para esta fecha.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Pedido</th>
                      <th className="px-4 py-3 text-left font-medium">Cliente</th>
                      <th className="px-4 py-3 text-left font-medium">Motivo</th>
                      <th className="px-4 py-3 text-right font-medium">Total</th>
                      <th className="px-4 py-3 text-left font-medium">Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasCanceladas.map((venta) => (
                      <tr key={venta.id} className="hover:bg-red-50 transition-colors opacity-70">
                        <td className="px-4 py-3">
                          <span className="font-mono font-medium text-red-500 line-through">{venta.numeroPedido}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{venta.cliente?.nombre}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-red-600">{venta.motivoCancelacion || 'Sin motivo'}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-500 line-through">${(venta.totales?.total || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {new Date(venta.fechas?.creadoEn).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-red-50 font-bold">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right">TOTAL ANULADO</td>
                      <td className="px-4 py-3 text-right text-red-500">${ventasCanceladas.reduce((sum, v) => sum + (v.totales?.total || 0), 0).toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          )}
        </div>
      </div>

      {/* Modal de Cierre de Caja */}
      {mostrarModalCierre && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl shadow-2xl p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                <Lock className="w-5 h-5 inline mr-2 text-emerald-600" />
                Cerrar Caja
              </h2>
              <button onClick={() => setMostrarModalCierre(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-sm mb-4 text-gray-500">
              Ingresa el dinero contado por cada método de pago para comparar con las ventas registradas.
            </p>

            {/* Tabla de comparación */}
            <div className="rounded-lg border overflow-hidden mb-4 border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Método</th>
                    <th className="px-4 py-2 text-right font-medium">Ventas</th>
                    <th className="px-4 py-2 text-right font-medium">Contado</th>
                    <th className="px-4 py-2 text-right font-medium">Diferencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { key: 'efectivo', label: 'Efectivo', ventas: resumen.totalEfectivo, icon: Banknote },
                    { key: 'tarjeta', label: 'Tarjetas', ventas: resumen.totalTarjeta, icon: CreditCard },
                    { key: 'transferencias', label: 'Transferencias', ventas: resumen.totalTransferencias, icon: Tag },
                    { key: 'nequi', label: 'Nequi / Daviplata', ventas: resumen.totalNequi, icon: Smartphone },
                  ].map((row) => {
                    const dif = comparacion[`dif${row.key.charAt(0).toUpperCase() + row.key.slice(1)}` as keyof typeof comparacion] as number;
                    const contadoValue = dineroContado[row.key as keyof typeof dineroContado];
                    const esRequerido = row.ventas > 0;
                    const estaVacio = contadoValue === '';
                    const falta = esRequerido && estaVacio;
                    return (
                      <tr key={row.key}>
                        <td className="px-4 py-2 font-medium flex items-center gap-2">
                          <row.icon className="w-4 h-4 text-gray-400" /> {row.label}
                        </td>
                        <td className="px-4 py-2 text-right">${row.ventas.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            value={contadoValue}
                            onChange={(e) => setDineroContado(prev => ({ ...prev, [row.key]: e.target.value }))}
                            className={`w-28 text-right text-sm px-2 py-1 rounded border outline-none bg-white text-gray-900 ${
                              falta ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-300'
                            }`}
                            placeholder={falta ? 'Requerido' : '0'}
                          />
                        </td>
                        <td className={`px-4 py-2 text-right font-bold ${getDiferenciaColor(dif)}`}>
                          {dif === 0 ? '—' : `${dif > 0 ? '+' : ''}${dif.toLocaleString()}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 font-bold border-t-2 border-gray-300">
                  <tr>
                    <td className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-right">${resumen.total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">${comparacion.totalContado.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right ${getDiferenciaColor(comparacion.diferenciaTotal)}`}>
                      {comparacion.diferenciaTotal === 0 ? 'CUADRE' : `${comparacion.diferenciaTotal > 0 ? '+' : ''}${comparacion.diferenciaTotal.toLocaleString()}`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Resultado */}
            {cierreGuardado && (
              <div className={`p-4 rounded-lg mb-4 text-center ${
                comparacion.diferenciaTotal === 0
                  ? 'bg-emerald-100 text-emerald-800'
                  : comparacion.diferenciaTotal > 0
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                <p className="text-lg font-bold">
                  {comparacion.diferenciaTotal === 0
                    ? 'Caja cuadrada'
                    : comparacion.diferenciaTotal > 0
                    ? `📥 Sobrante: $${comparacion.diferenciaTotal.toLocaleString()}`
                    : `⚠️ Faltante: $${Math.abs(comparacion.diferenciaTotal).toLocaleString()}`}
                </p>
                <p className="text-sm mt-1">
                  {comparacion.diferenciaTotal === 0
                    ? 'El dinero contado coincide con las ventas registradas.'
                    : comparacion.diferenciaTotal > 0
                    ? 'Hay más dinero del esperado. Verifica los ingresos.'
                    : 'Falta dinero respecto a las ventas registradas. Revisa los valores.'}
                </p>
              </div>
            )}

            {/* Validación de campos requeridos */}
            {(() => {
              const camposFaltantes = [
                { key: 'efectivo', label: 'Efectivo', ventas: resumen.totalEfectivo },
                { key: 'tarjeta', label: 'Tarjetas', ventas: resumen.totalTarjeta },
                { key: 'transferencias', label: 'Transferencias', ventas: resumen.totalTransferencias },
                { key: 'nequi', label: 'Nequi / Daviplata', ventas: resumen.totalNequi },
              ].filter(r => r.ventas > 0 && dineroContado[r.key as keyof typeof dineroContado] === '');
              if (camposFaltantes.length > 0) {
                return (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    Debes ingresar el dinero contado para: <strong>{camposFaltantes.map(c => c.label).join(', ')}</strong>
                  </div>
                );
              }
              return null;
            })()}

            {/* Botones */}
            <div className="flex gap-2">
              <button
                onClick={() => setMostrarModalCierre(false)}
                className="flex-1 py-2.5 rounded-lg border font-medium transition-colors text-sm border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarCierre}
                disabled={[
                  { key: 'efectivo', ventas: resumen.totalEfectivo },
                  { key: 'tarjeta', ventas: resumen.totalTarjeta },
                  { key: 'transferencias', ventas: resumen.totalTransferencias },
                  { key: 'nequi', ventas: resumen.totalNequi },
                ].some(r => r.ventas > 0 && dineroContado[r.key as keyof typeof dineroContado] === '')}
                className="flex-1 py-2.5 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] text-white rounded-lg font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm shadow-md"
              >
                Confirmar Cierre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de impresión */}
      {mostrarConfirmacionImprimir && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl shadow-2xl p-6 bg-white text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Printer className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              ¿Deseas imprimir el recibo?
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              El cierre de caja ha sido guardado exitosamente.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  imprimirTicket();
                  setMostrarConfirmacionImprimir(false);
                }}
                className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-colors border ${
                  opcionImprimirIndex === 0
                    ? 'bg-emerald-600 text-white border-emerald-600 ring-2 ring-emerald-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Sí, imprimir
              </button>
              <button
                onClick={() => {
                  setMostrarConfirmacionImprimir(false);
                }}
                className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors border ${
                  opcionImprimirIndex === 1
                    ? 'bg-emerald-600 text-white border-emerald-600 ring-2 ring-emerald-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                No
              </button>
            </div>
            <div className="mt-3 text-center text-[10px] text-gray-400">
              <span className="px-1 py-0.5 bg-gray-200 rounded text-gray-600">← →</span> Seleccionar · <span className="px-1 py-0.5 bg-gray-200 rounded text-gray-600">Enter</span> Confirmar
            </div>
          </div>
        </div>
      )}

      {/* Ticket de cierre (oculto, para impresión desde el modal) */}
      <TicketCierreCaja
        fecha={new Date().toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
        vendedor={user?.nombreCompleto || undefined}
        metodos={[
          { label: 'Efectivo', ventas: resumen.totalEfectivo, contado: comparacion.contadoEfectivo, diferencia: comparacion.difEfectivo },
          { label: 'Tarjetas', ventas: resumen.totalTarjeta, contado: comparacion.contadoTarjeta, diferencia: comparacion.difTarjeta },
          { label: 'Transferencias', ventas: resumen.totalTransferencias, contado: comparacion.contadoTransferencias, diferencia: comparacion.difTransferencias },
          { label: 'Nequi / Daviplata', ventas: resumen.totalNequi, contado: comparacion.contadoNequi, diferencia: comparacion.difNequi },
        ]}
        items={ventasEfectivas.flatMap(v => v.items || [])}
        totalVentas={resumen.total}
        totalContado={comparacion.totalContado}
        diferenciaTotal={comparacion.diferenciaTotal}
        cantidadVentas={resumen.cantidad}
      />
    </div>
  );
};
